import { FULL_TRACKS } from './full';
import { AEOLIAN, DORIAN, HIJAZ, hits } from './track-kit';
import type { MusicNotes, MusicPart, MusicTrack } from './types';

const PHRYGIAN = [0, 1, 3, 5, 7, 8, 10];

export const SOIREE_OUVERTURE: MusicTrack = {
  id: 'soiree-ouverture',
  name: "La soirée d'ouverture",
  rootMidi: 30,
  scale: PHRYGIAN,
  themeScale: HIJAZ,
  chords: [0, 1, 0, -1],
  kick: { fromHz: 320, release: 0.055 },
  parts: [
    {
      voice: 'rolling-bass',
      layer: 'bass',
      loopSteps: 4,
      octave: 0,
      followsChord: true,
      notes: hits([1, 2, 3]),
    },
    {
      voice: 'knock',
      layer: 'texture',
      loopSteps: 32,
      octave: 3,
      followsChord: false,
      notes: hits([3, 10, 19, 26, 29], 4),
      in: 'full',
    },
    {
      voice: 'chirp',
      layer: 'texture',
      loopSteps: 32,
      octave: 0,
      followsChord: false,
      notes: hits([7, 23]),
    },
    {
      voice: 'zap',
      layer: 'texture',
      loopSteps: 32,
      octave: 0,
      followsChord: false,
      notes: hits([15, 31]),
      in: 'full',
    },
    {
      voice: 'crickets',
      layer: 'texture',
      loopSteps: 32,
      octave: 0,
      followsChord: false,
      notes: hits([4, 12, 20, 28]),
      in: 'light',
    },
    {
      voice: 'arp',
      layer: 'arp',
      loopSteps: 8,
      octave: 2,
      followsChord: true,
      notes: [0, 2, 4, 7, 9, 7, 4, 2].map((degree, step) => [step, degree, 1]),
    },
    {
      voice: 'squelch',
      layer: 'squelch',
      loopSteps: 16,
      octave: 2,
      followsChord: true,
      notes: [
        [2, 0, 1],
        [3, 7, 1],
        [6, 1, 1],
        [10, 0, 1],
        [11, 4, 1],
        [14, 7, 1],
      ],
    },
    {
      voice: 'lead',
      layer: 'lead',
      loopSteps: 32,
      octave: 3,
      followsChord: false,
      notes: [
        [0, 7, 2],
        [3, 8, 1],
        [4, 7, 2],
        [6, 4, 2],
        [10, 5, 2],
        [12, 4, 4],
        [16, 7, 2],
        [19, 8, 1],
        [20, 9, 2],
        [22, 8, 2],
        [26, 7, 2],
        [28, 11, 4],
      ],
    },
    {
      voice: 'oriental',
      layer: 'theme',
      loopSteps: 64,
      octave: 2,
      followsChord: false,
      notes: [
        [0, 7, 3],
        [3, 8, 1],
        [4, 9, 2],
        [6, 8, 2],
        [8, 7, 4],
        [12, 6, 2],
        [14, 7, 2],
        [16, 8, 3],
        [19, 9, 1],
        [20, 10, 4],
        [24, 9, 2],
        [26, 8, 2],
        [28, 9, 4],
        [32, 11, 2],
        [34, 10, 1],
        [35, 9, 1],
        [36, 8, 1],
        [37, 9, 1],
        [38, 8, 1],
        [39, 7, 1],
        [40, 7, 6],
        [46, 9, 2],
        [48, 10, 2],
        [50, 9, 1],
        [51, 8, 1],
        [52, 6, 4],
        [56, 8, 2],
        [58, 9, 2],
        [60, 8, 2],
        [62, 7, 2],
      ],
    },
  ],
};

const BRUME_PLUCKS: MusicNotes = [
  [0, 7, 1],
  [3, 9, 1],
  [6, 11, 1],
  [8, 7, 1],
  [11, 9, 1],
  [14, 12, 1],
];

const BRUME_DROPLETS: MusicNotes = [
  [5, 14, 1],
  [13, 18, 1],
  [22, 16, 1],
  [30, 21, 1],
  [37, 14, 1],
  [46, 18, 1],
  [53, 16, 1],
  [61, 11, 1],
];

function brumeDroplets(oddBars: boolean): MusicNotes {
  return BRUME_DROPLETS.filter(([step]) => Math.floor(step / 16) % 2 === (oddBars ? 1 : 0));
}

export const BRUME_DU_LAC: MusicTrack = {
  id: 'brume-du-lac',
  name: 'Brume du lac',
  rootMidi: 26,
  scale: AEOLIAN,
  chords: [0, 0, -2, -1],
  kick: { fromHz: 230, release: 0.11 },
  padOpens: 300,
  parts: [
    {
      voice: 'round-bass',
      layer: 'bass',
      loopSteps: 4,
      octave: 0,
      followsChord: true,
      notes: [[2, 0, 1]],
    },
    {
      voice: 'ghost-bass',
      layer: 'bass',
      loopSteps: 4,
      octave: 1,
      followsChord: true,
      notes: [[3, 0, 1]],
    },
    {
      voice: 'lake-pluck',
      layer: 'kick',
      loopSteps: 16,
      octave: 2,
      followsChord: true,
      notes: BRUME_PLUCKS,
    },
    {
      voice: 'lake-pluck',
      layer: 'texture',
      loopSteps: 16,
      octave: 2,
      followsChord: true,
      notes: BRUME_PLUCKS,
      in: 'light',
    },
    {
      voice: 'droplet',
      layer: 'texture',
      loopSteps: 64,
      octave: 3,
      followsChord: false,
      notes: brumeDroplets(true),
    },
    {
      voice: 'droplet',
      layer: 'texture',
      loopSteps: 64,
      octave: 3,
      followsChord: false,
      notes: brumeDroplets(false),
      in: 'light',
    },
    {
      voice: 'droplet',
      layer: 'clap',
      loopSteps: 64,
      octave: 3,
      followsChord: false,
      notes: brumeDroplets(false),
    },
    {
      voice: 'mist-lead',
      layer: 'clap',
      loopSteps: 64,
      octave: 3,
      followsChord: false,
      notes: [
        [0, 11, 6],
        [6, 12, 2],
        [8, 11, 8],
        [16, 9, 10],
        [26, 8, 2],
        [28, 9, 4],
        [32, 11, 6],
        [38, 12, 2],
        [40, 14, 8],
        [48, 12, 8],
        [56, 11, 8],
      ],
    },
  ],
};

const HARMONIC_MINOR = [0, 2, 3, 5, 7, 8, 11];
const TRANCE_GATE = [0, 2, 3, 5, 6, 8, 10, 11, 13, 15];

const GOA_LEAD: MusicNotes = [
  [0, 7, 2],
  [2, 6, 2],
  [4, 7, 2],
  [6, 9, 2],
  [8, 10, 4],
  [12, 9, 2],
  [14, 7, 2],
  [16, 6, 4],
  [20, 4, 4],
  [24, 5, 2],
  [26, 6, 2],
  [28, 7, 4],
  [32, 7, 2],
  [34, 9, 2],
  [36, 10, 2],
  [38, 11, 2],
  [40, 12, 6],
  [46, 11, 2],
  [48, 10, 2],
  [50, 9, 2],
  [52, 10, 2],
  [54, 9, 2],
  [56, 7, 4],
  [60, 6, 4],
];

const GOA_DES_ETOILES: MusicTrack = {
  id: 'goa-des-etoiles',
  name: 'Goa des étoiles',
  rootMidi: 28,
  scale: HARMONIC_MINOR,
  chords: [0, -2, -3, 0],
  kick: { fromHz: 300, release: 0.06 },
  parts: [
    {
      voice: 'rolling-bass',
      layer: 'bass',
      loopSteps: 4,
      octave: 0,
      followsChord: true,
      notes: [
        [1, 0, 1],
        [2, 7, 1],
        [3, 0, 1],
      ],
    },
    ...[0, 2, 4].map((degree): MusicPart => ({
      voice: 'gate',
      layer: 'kick',
      loopSteps: 16,
      octave: 2,
      followsChord: true,
      notes: hits(TRANCE_GATE, degree),
    })),
    {
      voice: 'goa-lead',
      layer: 'lead',
      loopSteps: 64,
      octave: 3,
      followsChord: false,
      notes: GOA_LEAD.filter(([step]) => step >= 32),
    },
    {
      voice: 'goa-lead',
      layer: 'theme',
      loopSteps: 64,
      octave: 3,
      followsChord: false,
      notes: GOA_LEAD,
    },
    {
      voice: 'goa-arp',
      layer: 'clap',
      loopSteps: 8,
      octave: 3,
      followsChord: true,
      notes: [0, 2, 4, 6, 7, 6, 4, 2].map((degree, step) => [step, degree, 1]),
    },
    {
      voice: 'tom',
      layer: 'clap',
      loopSteps: 64,
      octave: 0,
      followsChord: false,
      notes: hits([60, 61, 62, 63]),
    },
  ],
};

const ACID_LINE = {
  loopSteps: 16,
  octave: 2,
  followsChord: true,
  notes: [
    [0, 0, 1],
    [1, 7, 1],
    [3, 2, 1],
    [4, 0, 1],
    [5, 4, 1],
    [6, 3, 1],
    [8, 0, 1],
    [9, 6, 1],
    [10, 7, 1],
    [11, 4, 1],
    [13, 2, 1],
    [14, 3, 1],
    [15, 0, 1],
  ],
  accents: [0, 5, 9, 13],
  slides: [3, 6, 10, 14],
} as const;

const GRENOUILLE_ACIDE: MusicTrack = {
  id: 'grenouille-acide',
  name: 'La Grenouille acide',
  rootMidi: 33,
  scale: DORIAN,
  chords: [0, 0, 0, -2],
  kick: { fromHz: 340, release: 0.05 },
  parts: [
    {
      voice: 'rolling-bass',
      layer: 'bass',
      loopSteps: 4,
      octave: 0,
      followsChord: true,
      notes: hits([1, 2, 3]),
    },
    { voice: 'acid', layer: 'kick', ...ACID_LINE },
    { voice: 'acid', layer: 'texture', ...ACID_LINE, in: 'light' },
    {
      voice: 'croak',
      layer: 'texture',
      loopSteps: 64,
      octave: 0,
      followsChord: false,
      notes: hits([23, 55]),
    },
    {
      voice: 'croak',
      layer: 'texture',
      loopSteps: 64,
      octave: 0,
      followsChord: false,
      notes: hits([11, 27, 43, 59]),
      in: 'light',
    },
    {
      voice: 'laser',
      layer: 'clap',
      loopSteps: 16,
      octave: 0,
      followsChord: false,
      notes: hits([9, 15]),
    },
  ],
};

export const TRACKS: readonly MusicTrack[] = [
  SOIREE_OUVERTURE,
  BRUME_DU_LAC,
  GRENOUILLE_ACIDE,
  GOA_DES_ETOILES,
  ...FULL_TRACKS,
];
